import { propEq, reject, takeLast } from 'ramda';
import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useReducer,
} from 'react';
import { uniqueRandomId } from '../../uniqueId';
import {
  INotification,
  INotificationAction,
  INotificatorContext,
  NotificationStateActionType,
} from './useNotificator.types';

const MAX_NOTIFICATION_COUNT = 1;

const NotificatorContext = createContext<INotificatorContext>({
  notifications: [],
  notify: () => {},
});

const notificationsReducer = (
  state: INotification[],
  action: INotificationAction,
) => {
  const {
    type,
    payload: { id, notification },
  } = action;

  switch (type) {
    case NotificationStateActionType.Add:
      return takeLast(
        MAX_NOTIFICATION_COUNT,
        notification ? [...state, notification] : state,
      );

    case NotificationStateActionType.Remove:
      return reject(propEq('id', id), state);

    default:
      return state;
  }
};

export const NotificatorProvider: FC = ({ children }) => {
  const [notifications, dispatch] = useReducer(notificationsReducer, []);

  const notify = useCallback(
    (NotificationComponent) => {
      const id = uniqueRandomId('notification');

      const notification: INotification = {
        id,
        NotificationComponent,
        dismiss: () =>
          dispatch({
            type: NotificationStateActionType.Remove,
            payload: { id },
          }),
      };

      dispatch({
        type: NotificationStateActionType.Add,
        payload: { notification },
      });
    },
    [dispatch],
  );

  return (
    <NotificatorContext.Provider value={{ notifications, notify }}>
      {children}
    </NotificatorContext.Provider>
  );
};

export const useNotificatorRenderer = () => {
  const { notifications } = useContext(NotificatorContext);
  return { notifications };
};

export const useNotificatorConsumer = () => {
  const { notify } = useContext(NotificatorContext);
  return { notify };
};
